import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CartDrawer from '@/components/CartDrawer';
import SEO from '@/components/SEO';

const PrivacyPolicyPage = () => {
    const { language } = useLanguage();
    const { isCartOpen, setIsCartOpen } = useCart();

    const sections = [
        {
            title: language === 'PL' ? '§ 1. Postanowienia ogólne i Administrator Danych' : '§ 1. General Provisions and Data Controller',
            content: language === 'PL' ? (
                <div className="space-y-3">
                    <p>1. Niniejsza Polityka Prywatności określa zasady przetwarzania i ochrony danych osobowych Użytkowników oraz Klientów sklepu internetowego „Włoska piwnica”.</p>
                    <p>2. Administratorem danych osobowych (ADO) zbieranych za pośrednictwem sklepu internetowego jest:</p>
                    <p className="font-semibold pl-4 border-l-2 border-primary/30">
                        RAIBA SPÓŁKA Z OGRANICZONĄ ODPOWIEDZIALNOŚCIĄ<br />
                        Adres siedziby: ul. Apenińska 5 / 11, 01-493 Warszawa, Polska<br />
                        NIP: 5223362903, REGON: 543961555, KRS: 0000982555<br />
                        Adres e-mail: <a href="mailto:kontakt@wloska-piwnica.pl" className="text-primary hover:underline">kontakt@wloska-piwnica.pl</a>
                    </p>
                    <p>3. Dane osobowe są przetwarzane zgodnie z rozporządzeniem RODO (Rozporządzenie Parlamentu Europejskiego i Rady (UE) 2016/679 z dnia 27 kwietnia 2016 r.).</p>
                </div>
            ) : (
                <div className="space-y-3">
                    <p>1. This Privacy Policy defines the rules for processing and protecting personal data of Users and Customers of the online store "Włoska piwnica".</p>
                    <p>2. The Data Controller (ADO) is:</p>
                    <p className="font-semibold pl-4 border-l-2 border-primary/30">
                        RAIBA SPÓŁKA Z OGRANICZONĄ ODPOWIEDZIALNOŚCIĄ<br />
                        Registered office address: ul. Apenińska 5 / 11, 01-493 Warsaw, Poland<br />
                        NIP: 5223362903, REGON: 543961555, KRS: 0000982555<br />
                        Email: <a href="mailto:kontakt@wloska-piwnica.pl" className="text-primary hover:underline">kontakt@wloska-piwnica.pl</a>
                    </p>
                </div>
            )
        },
        {
            title: language === 'PL' ? '§ 2. Cel i zakres zbierania danych osobowych' : '§ 2. Purpose and Scope of Data Collection',
            content: language === 'PL' ? (
                <div className="space-y-3">
                    <p>Dane osobowe Klientów są zbierane i przetwarzane wyłącznie w celach:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>Rejestracji i obsługi konta</strong> w sklepie (art. 6 ust. 1 lit. b RODO).</li>
                        <li><strong>Realizacji zamówień</strong> składanych w sklepie, w tym wystawiania faktur, kompletowania dostawy i kontaktu z Klientem (art. 6 ust. 1 lit. b RODO).</li>
                        <li><strong>Wypłacenia i rozliczenia środków</strong> (art. 6 ust. 1 lit. c RODO – obowiązki podatkowo-księgowe).</li>
                        <li><strong>Rozpatrywania reklamacji i zwrotów</strong> (art. 6 ust. 1 lit. b RODO).</li>
                        <li><strong>Weryfikacji wieku</strong> – ze względu na prawny obowiązek upewnienia się, że napoje alkoholowe sprzedawane są wyłącznie osobom powyżej 18. roku życia.</li>
                    </ul>
                </div>
            ) : (
                <div className="space-y-3">
                    <p>Personal data is processed for:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Account creation and registration.</li>
                        <li>Order fulfillment, invoicing, and logistics.</li>
                        <li>Tax and accounting obligations under Polish and EU laws.</li>
                        <li>Handling complaints and returns.</li>
                        <li>Age verification (ensuring customers are 18+).</li>
                    </ul>
                </div>
            )
        },
        {
            title: language === 'PL' ? '§ 3. Odbiorcy danych osobowych (Powierzenie przetwarzania)' : '§ 3. Data Recipients and Processors',
            content: language === 'PL' ? (
                <div className="space-y-3">
                    <p>1. W celu prawidłowego funkcjonowania sklepu internetowego oraz realizacji zamówień, Administrator przekazuje zebrane dane osobowe wyłącznie zaufanym podmiotom współpracującym:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>Polskie Płatności (Polskie ePłatności Sp. z o.o.</strong>, z siedzibą w Tajęcinie) – w celu realizacji płatności elektronicznych oraz kartami płatniczymi.</li>
                        <li><strong>Firmom kurierskim (InPost S.A., DHL Express Sp. z o.o.)</strong> – w celu dostarczenia zamówionych towarów do Klienta.</li>
                        <li>Biuro rachunkowe obsługujące Sklep – w celach księgowo-podatkowych.</li>
                        <li>Dostawcy usług hostingowych i systemów IT obsługujących sklep.</li>
                    </ul>
                    <p>2. Dane nie są przekazywane poza obszar Europejskiego Obszaru Gospodarczego (EOG).</p>
                </div>
            ) : (
                <div className="space-y-3">
                    <p>1. For the performance of the sales agreement, data is shared with:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>Polskie Płatności Sp. z o.o.</strong> – for payment processing.</li>
                        <li><strong>InPost S.A., DHL Express Sp. z o.o.</strong> – for shipping and logistics.</li>
                        <li>Accounting and IT services providers.</li>
                    </ul>
                </div>
            )
        },
        {
            title: language === 'PL' ? '§ 4. Prawa Użytkownika' : '§ 4. User Rights',
            content: language === 'PL' ? (
                <div className="space-y-3">
                    <p>Zgodnie z przepisami RODO, każdemu Klientowi przysługuje prawo do:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Dostępu do swoich danych osobowych oraz otrzymania ich kopii.</li>
                        <li>Sprostowania (poprawiania) swoich danych.</li>
                        <li>Usunięcia danych („prawo do bycia zapomnianym”) – o ile nie zachodzą inne przesłanki prawne nakazujące ADO przechowywanie danych (np. dokumentacja podatkowa).</li>
                        <li>Ograniczenia przetwarzania danych.</li>
                        <li>Przenoszenia danych.</li>
                        <li>Cofnięcia zgody na przetwarzanie w dowolnym momencie (bez wpływu na zgodność z prawem przetwarzania przed jej cofnięciem).</li>
                        <li>Wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych (PUODO).</li>
                    </ul>
                </div>
            ) : (
                <div className="space-y-3">
                    <p>Customers have the right to access, rectify, delete ("right to be forgotten"), restrict, or transfer their personal data, and file a complaint with PUODO.</p>
                </div>
            )
        },
        {
            title: language === 'PL' ? '§ 5. Pliki Cookies i technologie śledzące' : '§ 5. Cookies Policy',
            content: language === 'PL' ? (
                <div className="space-y-3">
                    <p>1. Serwis korzysta z plików cookies (ciasteczek) zapisywanych w pamięci urządzenia końcowego użytkownika.</p>
                    <p>2. Pliki cookies są niezbędne do prawidłowego działania sklepu (np. utrzymanie zawartości koszyka zakupowego, sesji logowania) oraz celów statystycznych i analitycznych.</p>
                    <p>3. Użytkownik ma możliwość zarządzania plikami cookies bezpośrednio z poziomu ustawień swojej przeglądarki internetowej (w tym ich blokowania i usuwania).</p>
                </div>
            ) : (
                <div className="space-y-3">
                    <p>The Website uses cookies to maintain sessions (e.g., shopping cart status) and for statistical and analytical purposes. You can block or delete them in your browser settings.</p>
                </div>
            )
        }
    ];

    return (
        <div className="min-h-screen bg-background text-foreground">
            <SEO 
                title={language === 'PL' ? "Polityka Prywatności - Włoska Piwnica" : "Privacy Policy - Włoska Piwnica"}
                description={language === 'PL' ? "Polityka prywatności sklepu Włoska Piwnica. Dowiedz się, w jaki sposób chronimy Twoje dane osobowe." : "Privacy policy of Włoska Piwnica store. Learn how we protect your personal data."}
                canonicalUrl="/privacy-policy"
            />
            <Header onCartClick={() => setIsCartOpen(true)} />

            <main className="container mx-auto px-4 pt-32 pb-12 md:pt-40 md:pb-20 max-w-4xl">
                {/* Header */}
                <div className="mb-16">
                    <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors mb-8 group">
                        <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
                        {language === 'PL' ? 'Powrót' : 'Back'}
                    </Link>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, type: "spring" }}
                        className="text-center space-y-6"
                    >
                        <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full mb-4">
                            <Shield className="h-10 w-10 text-primary" />
                        </div>
                        <h1 className="text-2xl sm:text-4xl md:text-5xl font-display font-bold tracking-tight">
                            {language === 'PL' ? 'Polityka Prywatności' : 'Privacy Policy'}
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            {language === 'PL' ? 'Obowiązuje od 27 maja 2026 r.' : 'Effective as of May 27, 2026'}
                        </p>
                    </motion.div>
                </div>

                {/* Content List */}
                <div className="space-y-12">
                    {sections.map((section, index) => (
                        <motion.section
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.05 }}
                            viewport={{ once: true }}
                            className="border-b border-border/50 pb-8 last:border-0"
                        >
                            <h2 className="mb-4 text-xl font-display font-semibold tracking-wide text-primary">
                                {section.title}
                            </h2>
                            <div className="text-muted-foreground leading-relaxed text-sm md:text-base">
                                {section.content}
                            </div>
                        </motion.section>
                    ))}
                </div>

            </main>

            <Footer />

            <CartDrawer
                isOpen={isCartOpen}
                onClose={() => setIsCartOpen(false)}
            />
        </div>
    );
};

export default PrivacyPolicyPage;
